import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEvent } from "../contexts/EventContext";
import { useGroupNavigation } from "../hooks/useGroupNavigation";
import { ListItem, ListItemFormData, Group, Person } from "../types";
import { apiService } from "../services/api";
import WishList from "../components/lists/WishList";
import NoEventState from "../components/events/NoEventState";
import DropdownSelect, {
  DropdownOption,
} from "../components/common/DropdownSelect";
import { GiftIcon, LightbulbIcon, HeartIcon } from "@phosphor-icons/react";

import "./pages.css";

const MyListsPage: React.FC = () => {
  const { currentGroupSlug, user, isAuthenticated } = useAuth();

  const { selectedEvent, events, isInitialized, refreshUserListStatus } = useEvent();
  const { navigateToGroup } = useGroupNavigation();

  const [items, setItems] = useState<ListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState<
    (Group & { persons: Person[]; isAdmin: boolean }) | null
  >(null);
  const [userPersons, setUserPersons] = useState<Person[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [hasActiveList, setHasActiveList] = useState<boolean>(false);
  const [activePersonsForEvent, setActivePersonsForEvent] = useState<Person[]>(
    []
  );
  const [currentList, setCurrentList] = useState<any>(null);

  useEffect(() => {
    if (isAuthenticated && currentGroupSlug) {
      loadGroupData();
    }
  }, [isAuthenticated, currentGroupSlug]);

  useEffect(() => {
    if (selectedPerson && selectedEvent) {
      loadItems();
    }
  }, [selectedPerson, selectedEvent]);

  // Check active persons when userPersons or selectedEvent changes
  useEffect(() => {
    if (userPersons.length > 0 && selectedEvent) {
      checkActivePersonsForEvent();
    }
  }, [userPersons, selectedEvent]);

  // Auto-select person when userPersons are loaded (fallback if no active persons check yet)
  useEffect(() => {
    if (
      userPersons.length > 0 &&
      !selectedPerson &&
      activePersonsForEvent.length === 0
    ) {
      // Try to restore from localStorage first
      const savedPersonId = localStorage.getItem("myListsSelectedPersonId");
      if (savedPersonId) {
        const savedPerson = userPersons.find(
          (p: Person) => p.id === parseInt(savedPersonId)
        );
        if (savedPerson) {
          setSelectedPerson(savedPerson);
          setSelectedPersonId(savedPerson.id);
          return;
        }
      }

      // Otherwise select the first person (usually the user themselves)
      const firstPerson = userPersons[0];
      setSelectedPerson(firstPerson);
      setSelectedPersonId(firstPerson.id);
      localStorage.setItem(
        "myListsSelectedPersonId",
        firstPerson.id.toString()
      );
    }
  }, [userPersons, selectedPerson, activePersonsForEvent]);

  // Create dropdown options for person selection (prefer active persons for current event)
  const personOptions: DropdownOption<string>[] = useMemo(() => {
    const personsToShow =
      activePersonsForEvent.length > 0 ? activePersonsForEvent : userPersons;
    return personsToShow.map((person) => ({
      value: person.id.toString(),
      label: person.parentPersonId ? `${person.name} (Bambino)` : person.name,
    }));
  }, [userPersons, activePersonsForEvent]);

  const loadGroupData = async () => {
    if (!currentGroupSlug || !user) return;

    try {
      const groupData = await apiService.getGroupDetails(currentGroupSlug);
      setGroup(groupData);

      // Trova le persone dell'utente corrente (inclusi i figli)
      const myPersons = groupData.persons.filter((p: Person) => {
        // Persone dell'utente stesso
        if (p.userId === user.id) return true;

        // Figli dell'utente (bambini il cui genitore ha lo stesso userId)
        if (p.parentPersonId) {
          const parent = groupData.persons.find(
            (parent: Person) => parent.id === p.parentPersonId
          );
          return parent && parent.userId === user.id;
        }

        return false;
      });

      setUserPersons(myPersons);
    } catch (error) {
      console.error("Failed to load group data:", error);
      setError("Errore nel caricamento dei dati del gruppo");
    }
  };

  // Check which user persons have active lists for the current event
  const checkActivePersonsForEvent = async () => {
    if (!selectedEvent || userPersons.length === 0) return;

    try {
      const activePersons: Person[] = [];

      for (const person of userPersons) {
        try {
          const listData = await apiService.getPersonListForEvent(
            selectedEvent.id,
            person.id
          );

          // If the API returns data (even if empty items), the person has an active list
          if (listData) {
            activePersons.push(person);
          }
        } catch (err) {
          // If API returns error, person doesn't have active list for this event
        }
      }

      setActivePersonsForEvent(activePersons);

      // Auto-select if only one person is active for this event
      if (
        activePersons.length === 1 &&
        (!selectedPerson ||
          !activePersons.find((p) => p.id === selectedPerson.id))
      ) {
        const activePerson = activePersons[0];
        setSelectedPerson(activePerson);
        setSelectedPersonId(activePerson.id);
        localStorage.setItem(
          "myListsSelectedPersonId",
          activePerson.id.toString()
        );
      }
    } catch (err) {
      console.error("Error checking active persons for event:", err);
    }
  };

  const loadItems = async () => {
    if (!selectedPerson || !selectedEvent) return;

    try {
      setIsLoading(true);
      setError(null);

      // Use event-based API to get person's list for the selected event
      const listData = await apiService.getPersonListForEvent(
        selectedEvent.id,
        selectedPerson.id
      );

      if (listData && listData.items) {
        // Use items as they come from backend - they already have correct isReserved/reservedByMe
        setItems(listData.items);
        setHasActiveList(true);
        setCurrentList(listData);
      } else {
        setItems([]);
        setHasActiveList(false);
        setCurrentList(null);
      }
    } catch (err) {
      setError("Errore nel caricamento delle liste");
      console.error("Error loading items:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemAdd = async (itemData: ListItemFormData) => {
    if (!selectedPerson || !selectedEvent) return;

    try {
      // Use event-based API to create item for the selected event
      const newItem = await apiService.createListItemForEventPerson(
        selectedEvent.id,
        selectedPerson.id,
        itemData
      );
      setItems((prev) => [...prev, newItem]);
      // Refresh user list status to update the banner
      refreshUserListStatus();
    } catch (err: any) {
      // Let WishList handle the error with notifications - no need for banner
      throw err;
    }
  };

  const handleItemUpdate = async (
    id: number,
    itemData: Partial<ListItemFormData>
  ) => {
    try {
      const updatedItem = await apiService.updateListItem(id, itemData);
      setItems((prev) =>
        prev.map((item) => (item.id === id ? updatedItem : item))
      );
      // Refresh user list status to update the banner
      refreshUserListStatus();
    } catch (err: any) {
      // Let WishList handle the error with notifications - no need for banner
      throw err;
    }
  };

  const handleItemDelete = async (id: number) => {
    try {
      await apiService.deleteListItem(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      // Refresh user list status to update the banner
      refreshUserListStatus();
    } catch (err: any) {
      // Let WishList handle the error with notifications - no need for banner
      throw err;
    }
  };

  const handlePersonSelect = (personId: number) => {
    const person = userPersons.find((p) => p.id === personId);
    if (person) {
      setSelectedPerson(person);
      setSelectedPersonId(personId);
      localStorage.setItem("myListsSelectedPersonId", personId.toString());
    }
  };

  const handlePersonDropdownChange = (personIdStr: string) => {
    const personId = parseInt(personIdStr);
    handlePersonSelect(personId);
  };

  const handleListPublish = () => {
    // Reload the list data to get updated lock status
    loadItems();
  };

  // Se non ci sono persone per questo utente
  if (userPersons.length === 0 && group) {
    return (
      <div className="my-lists-page">
        <div className="error-container">
          <h2>Nessuna persona associata</h2>
          <p>Non hai persone associate al tuo account in questo gruppo.</p>
          <a href="/dashboard" className="action-button secondary">
            Torna alla dashboard
          </a>
        </div>
      </div>
    );
  }

  // Show the same event-empty state used across the event-dependent pages.
  if (isInitialized && !selectedEvent && group) {
    return (
      <div className="my-lists-page">
        <NoEventState
          isAdmin={group.isAdmin}
          hasExistingEvents={events.length > 0}
          onCreateEvent={
            group.isAdmin ? () => navigateToGroup("/admin#events") : undefined
          }
        />
      </div>
    );
  }

  // Se l'utente ha più persone ma non ne ha selezionata una
  if (userPersons.length > 1 && !selectedPerson) {
    return (
      <div className="my-lists-page">
        <div className="person-selection-container">
          <div className="person-selection-header">
            <h2>
              <GiftIcon size={24} weight="fill" />
              Le Mie Liste Regalo
            </h2>
            <p>Seleziona per chi vuoi gestire le liste regalo</p>
          </div>

          <div className="person-selection-dropdown">
            <label htmlFor="person-select" className="dropdown-label">
              Gestisci liste per:
            </label>
            <DropdownSelect
              options={personOptions}
              value={selectedPersonId?.toString() || ""}
              onChange={handlePersonDropdownChange}
              placeholder="Seleziona una persona"
              className="person-dropdown"
            />
          </div>

          <div className="person-selection-info">
            <p>
              <LightbulbIcon size={16} weight="fill" />
              Puoi gestire le liste per te stesso e per i bambini associati al
              tuo account
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="my-lists-page">
        <div className="loading-container">
          <p>Caricamento delle tue liste...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-lists-page">
      <div className="modern-page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>
              <GiftIcon size={32} weight="fill" />
              La Mia Lista Regalo
            </h1>

            {userPersons.length === 1 && (
              <div>
                <p>
                  Gestisci la tua lista dei desideri{" "}
                  <HeartIcon size={16} weight="fill" />
                </p>
              </div>
            )}
            {userPersons.length > 1 && (
              <div>
                <p>Gestisci la lista dei desideri per: </p>
                <span className="person-switcher-inline">
                  <DropdownSelect
                    options={personOptions}
                    value={selectedPersonId?.toString() || ""}
                    onChange={handlePersonDropdownChange}
                    className="person-dropdown-inline"
                  />
                </span>
              </div>
            )}
          </div>
          <div className="header-decoration">
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="dismiss-button">
            Chiudi
          </button>
        </div>
      )}

      {selectedPerson && (
        <>
          {!hasActiveList && items.length === 0 && (
            <div className="inactive-list-message">
              <div className="message-content">
                <h3>
                  Purtroppo non puoi scegliere i tuoi regali per l'evento{" "}
                  <strong>{selectedEvent?.name}</strong>.
                </h3>
                <p>
                  Scegli un altro evento o contatta l'amministratore del gruppo
                  per attivare la tua lista regalo per questo evento.
                </p>
              </div>
            </div>
          )}

          {hasActiveList && (
            <WishList
              personId={selectedPerson.id}
              eventId={selectedEvent?.id}
              items={items}
              canEdit={hasActiveList}
              onItemAdd={hasActiveList ? handleItemAdd : undefined}
              onItemUpdate={handleItemUpdate}
              onItemDelete={handleItemDelete}
              listId={currentList?.id}
              isLocked={currentList?.isLocked}
              onListPublish={() => {
                handleListPublish();
                loadItems(); // Reload items after publishing
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MyListsPage;
